import type { CvssMetrics, Cvss4Metrics, CvssVersion } from './cvss-types';
import {
  areAllMetricsSelected,
  calculateCvssBaseScoreFromVector,
  calculateCvssScore,
  generateVectorString,
  isCvss4Metrics,
  parseVectorString,
} from './cvss-utils';
import { calculateAeOverallScore } from './cvss-ae-calculator';
import type {
  CvssEnvironmentalRequirements,
  CvssEnvironmentalScoreResult,
  CvssModifiedBaseMetrics,
  CvssSecurityRequirementMetricKey,
} from './cvss-environmental-types';
import {
  CVSS_31_MODIFIED_BASE_METRIC_KEYS,
  CVSS_31_MODIFIED_METRIC_ALLOWED_VALUES,
  CVSS_40_MODIFIED_BASE_METRIC_KEYS,
  CVSS_40_MODIFIED_METRIC_ALLOWED_VALUES,
  CVSS_MODIFIED_BASE_METRIC_KEYS,
  CVSS_MODIFIED_BASE_VECTOR_KEYS,
  CVSS_MODIFIED_METRIC_ALLOWED_VALUES,
  CVSS_SECURITY_REQUIREMENT_METRIC_KEYS,
  CVSS_SECURITY_REQUIREMENT_VECTOR_KEYS,
  DEFAULT_CVSS_MODIFIED_BASE_METRICS,
  isCvssSecurityRequirementValue,
  resolveModifiedMetricValue,
} from './cvss-environmental-types';

type CvssEnvironmentalVersion = Exclude<CvssVersion, '3.0'> | 'V3_1' | 'V4_0';

const isCvss40Version = (version: CvssEnvironmentalVersion): boolean =>
  version === '4.0' || version === 'V4_0';

export const normalizeEnvironmentalRequirements = (
  requirements: Partial<CvssEnvironmentalRequirements> | null | undefined,
): CvssEnvironmentalRequirements => {
  const securityRequirements = Object.fromEntries(
    CVSS_SECURITY_REQUIREMENT_METRIC_KEYS.map((key) => {
      const value = requirements?.[key];
      return [
        key,
        value && isCvssSecurityRequirementValue(value) ? value : 'X',
      ];
    }),
  ) as Pick<CvssEnvironmentalRequirements, CvssSecurityRequirementMetricKey>;

  return {
    ...securityRequirements,
    mav: resolveModifiedMetricValue({ key: 'mav', value: requirements?.mav }),
    mac: resolveModifiedMetricValue({ key: 'mac', value: requirements?.mac }),
    mat: resolveModifiedMetricValue({ key: 'mat', value: requirements?.mat }),
    mpr: resolveModifiedMetricValue({ key: 'mpr', value: requirements?.mpr }),
    mui: resolveModifiedMetricValue({ key: 'mui', value: requirements?.mui }),
    ms: resolveModifiedMetricValue({ key: 'ms', value: requirements?.ms }),
    mc: resolveModifiedMetricValue({ key: 'mc', value: requirements?.mc }),
    mi: resolveModifiedMetricValue({ key: 'mi', value: requirements?.mi }),
    ma: resolveModifiedMetricValue({ key: 'ma', value: requirements?.ma }),
    mvc: resolveModifiedMetricValue({ key: 'mvc', value: requirements?.mvc }),
    mvi: resolveModifiedMetricValue({ key: 'mvi', value: requirements?.mvi }),
    mva: resolveModifiedMetricValue({ key: 'mva', value: requirements?.mva }),
    msc: resolveModifiedMetricValue({ key: 'msc', value: requirements?.msc }),
    msi: resolveModifiedMetricValue({ key: 'msi', value: requirements?.msi }),
    msa: resolveModifiedMetricValue({ key: 'msa', value: requirements?.msa }),
  };
};

export const environmentalRequirementsForVersion = ({
  requirements,
  version,
}: {
  requirements: Partial<CvssEnvironmentalRequirements> | null | undefined;
  version: CvssEnvironmentalVersion;
}): CvssEnvironmentalRequirements => {
  const normalized = normalizeEnvironmentalRequirements(requirements);
  const isV40 = isCvss40Version(version);
  const versionKeys = new Set<string>(
    isV40
      ? CVSS_40_MODIFIED_BASE_METRIC_KEYS
      : CVSS_31_MODIFIED_BASE_METRIC_KEYS,
  );
  const versionAllowedValues = isV40
    ? CVSS_40_MODIFIED_METRIC_ALLOWED_VALUES
    : CVSS_31_MODIFIED_METRIC_ALLOWED_VALUES;

  const modified = Object.fromEntries(
    CVSS_MODIFIED_BASE_METRIC_KEYS.map((key) => {
      if (!versionKeys.has(key)) {
        return [key, DEFAULT_CVSS_MODIFIED_BASE_METRICS[key]];
      }
      const value = normalized[key];
      const allowed = (
        versionAllowedValues as Record<string, readonly string[]>
      )[key];
      if (allowed?.includes(value)) {
        return [key, value];
      }
      return [key, DEFAULT_CVSS_MODIFIED_BASE_METRICS[key]];
    }),
  ) as CvssModifiedBaseMetrics;

  return {
    cr: normalized.cr,
    ir: normalized.ir,
    ar: normalized.ar,
    ...modified,
  };
};

export const isModifiedBaseConfigured = (
  requirements: Partial<CvssEnvironmentalRequirements> | null | undefined,
): boolean => {
  if (!requirements) {
    return false;
  }
  const resolved = normalizeEnvironmentalRequirements(requirements);
  return CVSS_MODIFIED_BASE_METRIC_KEYS.some(
    (key) => resolved[key] && resolved[key] !== 'X',
  );
};

export const isEnvironmentalRequirementsConfigured = (
  requirements: Partial<CvssEnvironmentalRequirements> | null | undefined,
): boolean => {
  if (!requirements) {
    return false;
  }
  const resolved = normalizeEnvironmentalRequirements(requirements);
  return (
    resolved.cr !== 'X' ||
    resolved.ir !== 'X' ||
    resolved.ar !== 'X' ||
    isModifiedBaseConfigured(resolved)
  );
};

export const toSharedSecurityRequirementsOnly = (
  requirements: Partial<CvssEnvironmentalRequirements> | null | undefined,
): CvssEnvironmentalRequirements => {
  const resolved = normalizeEnvironmentalRequirements(requirements);
  return {
    ...DEFAULT_CVSS_MODIFIED_BASE_METRICS,
    cr: resolved.cr,
    ir: resolved.ir,
    ar: resolved.ar,
  };
};

export const formatEnvironmentalVector = (
  requirements: Partial<CvssEnvironmentalRequirements>,
  version?: CvssEnvironmentalVersion,
): string => {
  const resolved = version
    ? environmentalRequirementsForVersion({ requirements, version })
    : normalizeEnvironmentalRequirements(requirements);
  const parts: string[] = [];

  for (const key of CVSS_SECURITY_REQUIREMENT_METRIC_KEYS) {
    const value = resolved[key];
    if (value && value !== 'X') {
      parts.push(`${CVSS_SECURITY_REQUIREMENT_VECTOR_KEYS[key]}:${value}`);
    }
  }

  for (const key of CVSS_MODIFIED_BASE_METRIC_KEYS) {
    const value = resolved[key];
    if (value && value !== 'X') {
      parts.push(`${CVSS_MODIFIED_BASE_VECTOR_KEYS[key]}:${value}`);
    }
  }

  return parts.join('/');
};

export const parseEnvironmentalVector = (
  vector: string | null | undefined,
): Partial<CvssEnvironmentalRequirements> => {
  if (!vector || typeof vector !== 'string') {
    return {};
  }

  const parsed: Partial<CvssEnvironmentalRequirements> = {};
  const securityRequirementVectorKeyToField = Object.fromEntries(
    Object.entries(CVSS_SECURITY_REQUIREMENT_VECTOR_KEYS).map(
      ([field, vectorKey]) => [vectorKey, field],
    ),
  ) as Record<string, CvssSecurityRequirementMetricKey>;
  const vectorKeyToField = Object.fromEntries(
    Object.entries(CVSS_MODIFIED_BASE_VECTOR_KEYS).map(([field, vectorKey]) => [
      vectorKey,
      field,
    ]),
  ) as Record<string, keyof CvssModifiedBaseMetrics>;

  for (const part of vector.split('/')) {
    const [key, value] = part.split(':');
    if (!key || !value) {
      continue;
    }
    const securityRequirementField = securityRequirementVectorKeyToField[key];
    if (securityRequirementField && isCvssSecurityRequirementValue(value)) {
      parsed[securityRequirementField] = value;
      continue;
    }
    const modifiedField = vectorKeyToField[key];
    if (
      modifiedField &&
      (
        CVSS_MODIFIED_METRIC_ALLOWED_VALUES[modifiedField] as readonly string[]
      ).includes(value)
    ) {
      Object.assign(parsed, {
        [modifiedField]: resolveModifiedMetricValue({
          key: modifiedField,
          value,
        }),
      });
    }
  }
  return parsed;
};

export const extractEnvironmentalRequirementsFromVector = ({
  vector,
  environmentalVector,
}: {
  vector?: string | null;
  environmentalVector?: string | null;
}): Partial<CvssEnvironmentalRequirements> => ({
  ...parseEnvironmentalVector(vector),
  ...parseEnvironmentalVector(environmentalVector),
});

export const resolveEnvironmentalRequirementsForFindingVersion = ({
  requirements,
  findingVersion,
  applicationVersion,
  previousVector,
  previousEnvironmentalVector,
}: {
  requirements: Partial<CvssEnvironmentalRequirements> | null | undefined;
  findingVersion: CvssVersion;
  applicationVersion?: CvssVersion;
  previousVector?: string | null;
  previousEnvironmentalVector?: string | null;
}): CvssEnvironmentalRequirements => {
  const resolvedFindingVersion =
    findingVersion === '3.0' ? '3.1' : findingVersion;

  if (
    applicationVersion == null ||
    findingVersion === applicationVersion ||
    (findingVersion === '3.0' && applicationVersion === '3.1')
  ) {
    return environmentalRequirementsForVersion({
      requirements,
      version: resolvedFindingVersion,
    });
  }

  const extracted = extractEnvironmentalRequirementsFromVector({
    vector: previousVector,
    environmentalVector: previousEnvironmentalVector,
  });

  return environmentalRequirementsForVersion({
    requirements: extracted,
    version: resolvedFindingVersion,
  });
};

const buildEnvironmentalVector = ({
  baseVector,
  requirements,
}: {
  baseVector: string;
  requirements: CvssEnvironmentalRequirements;
}): string => {
  const environmentalParts = formatEnvironmentalVector(requirements);
  return environmentalParts
    ? `${baseVector}/${environmentalParts}`
    : baseVector;
};

const resolveBaseVector = ({
  version,
  baseVector,
  metrics,
}: {
  version: Exclude<CvssVersion, '3.0'> | '3.0';
  baseVector?: string | null;
  metrics: CvssMetrics | Cvss4Metrics;
}): string => {
  if (baseVector) {
    return baseVector;
  }
  if (version === '4.0' || isCvss4Metrics(metrics)) {
    return generateVectorString(metrics as Cvss4Metrics, '4.0');
  }
  return generateVectorString(
    metrics as CvssMetrics,
    version === '3.0' ? '3.0' : '3.1',
  );
};

export const calculateEnvironmentalScore = ({
  version,
  baseVector,
  baseMetrics,
  requirements,
}: {
  version: Exclude<CvssVersion, '3.0'> | '3.0';
  baseVector?: string | null;
  baseMetrics?: CvssMetrics | Cvss4Metrics;
  requirements?: Partial<CvssEnvironmentalRequirements> | null;
}): CvssEnvironmentalScoreResult | null => {
  const resolvedRequirements =
    version === '3.0'
      ? normalizeEnvironmentalRequirements(requirements)
      : environmentalRequirementsForVersion({ requirements, version });

  let metrics: CvssMetrics | Cvss4Metrics | undefined = baseMetrics;
  if (!metrics && baseVector) {
    const parsed = parseVectorString(baseVector);
    if (version === '4.0') {
      metrics = parsed as Cvss4Metrics;
    } else {
      metrics = parsed as CvssMetrics;
    }
  }

  if (!metrics || !areAllMetricsSelected(metrics)) {
    if (baseVector) {
      const fromVector = calculateCvssBaseScoreFromVector(baseVector);
      if (fromVector === null) {
        return null;
      }
      return {
        baseScore: fromVector,
        environmentalScore: fromVector,
        environmentalVector: null,
        effectiveScore: fromVector,
        isEnvironmentalConfigured: false,
      };
    }
    return null;
  }

  const resolvedBaseVector = resolveBaseVector({
    version,
    baseVector,
    metrics,
  });
  const baseScore = calculateCvssScore(metrics);
  const configured =
    isEnvironmentalRequirementsConfigured(resolvedRequirements);

  if (!configured) {
    return {
      baseScore,
      environmentalScore: baseScore,
      environmentalVector: null,
      effectiveScore: baseScore,
      isEnvironmentalConfigured: false,
    };
  }

  const environmentalVector = formatEnvironmentalVector(
    resolvedRequirements,
    version === '3.0' ? '3.1' : version,
  );
  const environmentalScore =
    calculateAeOverallScore(
      buildEnvironmentalVector({
        baseVector: resolvedBaseVector,
        requirements: resolvedRequirements,
      }),
    ) ?? baseScore;

  return {
    baseScore,
    environmentalScore,
    environmentalVector,
    effectiveScore: environmentalScore,
    isEnvironmentalConfigured: true,
  };
};

export const stripEnvironmentalMetricsFromVector = (vector: string): string => {
  if (!vector || typeof vector !== 'string') {
    return vector;
  }

  return vector
    .split('/')
    .filter((part) => {
      const key = part.split(':')[0];
      return (
        key !== 'CR' &&
        key !== 'IR' &&
        key !== 'AR' &&
        key !== 'MC' &&
        key !== 'MI' &&
        key !== 'MA' &&
        key !== 'MS' &&
        key !== 'MAV' &&
        key !== 'MAC' &&
        key !== 'MAT' &&
        key !== 'MPR' &&
        key !== 'MUI' &&
        key !== 'MVC' &&
        key !== 'MVI' &&
        key !== 'MVA' &&
        key !== 'MSC' &&
        key !== 'MSI' &&
        key !== 'MSA' &&
        key !== 'E' &&
        key !== 'RL' &&
        key !== 'RC'
      );
    })
    .join('/');
};
